import { requiredRule, useFormControl } from "@mongez/react-form";
import { cn } from "core/shared";
import {
  defaultCountries,
  ParsedCountry,
  PhoneInput,
} from "react-international-phone";
import "react-international-phone/style.css";
import { ErrorInput } from "~/design-system/components/form/ErrorInput";
import { LabelInput } from "~/design-system/components/form/LabelInput";
import { ClientOnly } from "../ClientOnly";
import { BaseInputProps } from "./BaseInput";

// const { PhoneNumberUtil, PhoneNumberType } = pkg;

// const phoneUtil = PhoneNumberUtil.getInstance();

// Define rounded classes based on the rounded prop
const roundedClasses = {
  sm: {
    container: "!rounded-sm",
    countrySelector: "!rounded-s-sm",
    input: "!rounded-e-sm",
  },
  md: {
    container: "!rounded-md",
    countrySelector: "!rounded-s-md",
    input: "!rounded-e-md",
  },
  lg: {
    container: "!rounded-lg",
    countrySelector: "!rounded-s-lg",
    input: "!rounded-e-lg",
  },
  xl: {
    container: "!rounded-xl",
    countrySelector: "!rounded-s-xl",
    input: "!rounded-e-xl",
  },
  full: {
    container: "!rounded-full",
    countrySelector: "!rounded-s-full",
    input: "!rounded-e-full",
  },
};

type IntlTelInputProps = {
  name: string;
  id: string;
  defaultCountry?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  rounded?: "sm" | "md" | "lg" | "xl" | "full";
  loadingFallback?: React.ReactNode;
  classes?: {
    wrapperClassName?: string;
    error?: string;
    className?: string;
    input?: string;
    countrySelector?: string;
  };
} & BaseInputProps;

export function PhoneNumberInput({
  defaultCountry = "us",
  defaultValue,
  onChange,
  placeholder,
  label,
  id,
  required,
  classes,
  rounded = "full",
  loadingFallback,
  ...props
}: IntlTelInputProps) {
  const { changeValue, value, name, error } = useFormControl({
    ...props,
    defaultValue,
    required,
    rules: [requiredRule],
  });

  const handleChange = (newValue: string, country: ParsedCountry) => {
    const dialCode = country.dialCode;
    const userHasInput = newValue.length > dialCode.length;

    if (userHasInput) {
      changeValue(newValue);
    } else if (value !== dialCode && value !== "") {
      changeValue("");
    }

    if (onChange) {
      onChange(newValue);
    }
  };

  return (
    <div
      className={cn("flex w-full flex-col gap-2", classes?.wrapperClassName)}
      dir="ltr"
    >
      <LabelInput htmlFor={id} required={required} className={classes?.label}>
        {label}
      </LabelInput>

      <ClientOnly
        fallback={
          loadingFallback || (
            <div className="h-[58px] w-full animate-pulse rounded-full border border-neutral-300 bg-gray-100" />
          )
        }
      >
        <PhoneInput
          inputProps={{
            placeholder,
            id,
            name,
            className: cn(
              "rounded-full bg-transparent ltr:pl-16 w-full rtl:pr-16 px-5 py-4 placeholder:text-sm transition-colors placeholder:text-neutral-400 outline-none border border-night focus:border-primary-500",
              classes?.input,
              error ? "border-red-500" : "",
            ),
            type: "tel",
          }}
          countrySelectorStyleProps={{
            flagClassName: "size-8",
            buttonClassName: cn(
              "!border-none !rounded-lg absolute top-1/2 -translate-y-1/2 ltr:left-3 rtl:right-3 h-[50px] w-11 ltr:pl-4 rtl:pr-4 bg-transparent",
              classes?.countrySelector,
            ),
          }}
          value={value}
          onChange={(phone, { country }) => {
            handleChange(phone, country);
          }}
          countries={defaultCountries}
          defaultCountry={defaultCountry}
          disabled={props.disabled}
          prefix=""
          {...props}
        />
      </ClientOnly>

      <ErrorInput className={classes?.error}>{error}</ErrorInput>
    </div>
  );
}
